import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Modal, Select, Switch, Tag } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { ArrowForward, CheckCircleOutline, RadioButtonUnchecked } from '@material-ui/icons';
import toast from 'components/Basic/Toast';
import Loading from 'components/Basic/Loading';
import {
  useLazyGetIntegrationQuery,
  useLazyListZigiBusinessesQuery,
  useRegisterZigiWebhookMutation,
  useUpdateIntegrationMutation,
} from 'core/integrations/IntegrationsService';
import {
  ZIGI_APP_RETURN_URL,
  ZIGI_BUSINESS_CODE_MAX_LENGTH,
  ZIGI_CURRENCY_LENGTH,
  ZIGI_MASKED_VALUE,
  ZIGI_MAX_RECOMMENDED_EXPIRY_MINUTES,
  ZIGI_PLAIN_KEYS,
  ZIGI_REQUIRED_TO_ENABLE,
  ZIGI_SECRET_KEYS,
  ZIGI_SLUG,
  ZIGI_WEBHOOK_DUPLICATE_CODE,
  ZIGI_WEBHOOK_KEY_MISSING_CODE,
  ZIGI_WEBHOOK_PATH,
  ZIGI_WEBHOOK_URL_MAX_LENGTH,
  ZigiAdminError,
  ZigiBusiness,
  ZigiIntegrationResponse,
  ZigiKey,
  ZigiPlainKey,
  ZigiSecretKey,
  ZigiStoredValue,
} from 'core/integrations/zigiTypes';
import { isApiUrl, isHttpsUrl, isLocalHost, isReturnUrl, normalizePlain } from 'core/integrations/zigiValidation';

const { Option } = Select;

type PlainValues = Record<ZigiPlainKey, string>;
type SecretValues = Record<ZigiSecretKey, string>;
type SecretStatus = 'configured' | 'missing';
type ChangedSettings = Partial<Record<ZigiPlainKey, string | number> & Record<ZigiSecretKey, string>>;

const CURRENCY_REGEX = /^[A-Za-z]{3}$/;
const POSITIVE_INT_REGEX = /^[1-9]\d*$/;
const cardStyle = { borderRadius: 8 };
const fieldStyle = { marginBottom: 14 };

const recordFrom = <K extends string, V>(keys: ReadonlyArray<K>, value: (key: K) => V): Record<K, V> =>
  keys.reduce((acc, key) => ({ ...acc, [key]: value(key) }), {} as Record<K, V>);

const emptyPlain = (): PlainValues => recordFrom(ZIGI_PLAIN_KEYS, () => '');
const emptySecrets = (): SecretValues => recordFrom(ZIGI_SECRET_KEYS, () => '');

const isSecretKey = (key: ZigiKey): key is ZigiSecretKey => (ZIGI_SECRET_KEYS as ReadonlyArray<ZigiKey>).includes(key);

// Stored secrets come back masked, or as a reference object when they live in an environment variable.
const isStoredSecret = (value: ZigiStoredValue | undefined): boolean =>
  value === ZIGI_MASKED_VALUE || (typeof value === 'object' && value !== null);

const defaultWebhookUrl = (): string => {
  try {
    return new URL(ZIGI_WEBHOOK_PATH, process.env.REACT_APP_SERVER).toString();
  } catch {
    return '';
  }
};

const ZigiConfig: React.FC = () => {
  const intl = useIntl();

  const [enabled, setEnabled] = useState(false);
  const [initialEnabled, setInitialEnabled] = useState(false);
  const [values, setValues] = useState<PlainValues>(emptyPlain);
  const [initialValues, setInitialValues] = useState<PlainValues>(emptyPlain);
  const [secrets, setSecrets] = useState<SecretValues>(emptySecrets);
  const [secretStatus, setSecretStatus] = useState<Record<ZigiSecretKey, SecretStatus>>(() =>
    recordFrom(ZIGI_SECRET_KEYS, () => 'missing'),
  );
  const [businesses, setBusinesses] = useState<ZigiBusiness[] | null>(null);
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const [getIntegration] = useLazyGetIntegrationQuery();
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [listBusinesses] = useLazyListZigiBusinessesQuery();
  const [registerWebhook] = useRegisterZigiWebhookMutation();

  const withLoading = useCallback(async (task: () => Promise<unknown>) => {
    setIsLoading(true);
    try {
      await task();
    } finally {
      setIsLoading(false);
    }
  }, []);

  const applyResponse = useCallback((res: ZigiIntegrationResponse) => {
    const settings = res.settings ?? {};
    const plain = recordFrom(ZIGI_PLAIN_KEYS, key => {
      const value = settings[key];
      return value == null || typeof value === 'object' ? '' : normalizePlain(key, String(value));
    });

    setEnabled(res.enabled);
    setInitialEnabled(res.enabled);
    setValues(plain);
    setInitialValues(plain);
    setSecrets(emptySecrets());
    setSecretStatus(recordFrom(ZIGI_SECRET_KEYS, key => (isStoredSecret(settings[key]) ? 'configured' : 'missing')));
  }, []);

  const load = useCallback(() => {
    setLoadError(false);
    withLoading(() =>
      getIntegration({ slug: ZIGI_SLUG })
        .unwrap()
        .then(res => applyResponse(res as ZigiIntegrationResponse))
        .catch(() => setLoadError(true)),
    );
  }, [getIntegration, withLoading, applyResponse]);

  useEffect(() => {
    load();
  }, [load]);

  const errors: Partial<Record<ZigiPlainKey, string>> = {};
  const warnings: Partial<Record<ZigiPlainKey, string>> = {};
  ZIGI_PLAIN_KEYS.forEach(key => {
    const value = values[key].trim();
    if (!value) return;
    if (key === 'apiUrl' && !isApiUrl(value, isLocalHost(window.location.hostname))) {
      errors[key] = intl.formatMessage({ id: 'The URL must start with https://' });
    }
    if (key === 'redirectUrl' && !isReturnUrl(value)) {
      errors[key] = intl.formatMessage({ id: 'Please enter an https:// URL or an app link.' });
    }
    if (key === 'businessCode' && value.length > ZIGI_BUSINESS_CODE_MAX_LENGTH) {
      errors[key] = intl.formatMessage({ id: 'Maximum {max} characters.' }, { max: ZIGI_BUSINESS_CODE_MAX_LENGTH });
    }
    if (key === 'currency' && !CURRENCY_REGEX.test(value)) {
      errors[key] = intl.formatMessage({ id: 'Please enter a 3-letter currency code.' });
    }
    if (key === 'expiryTimeMinutes') {
      if (!POSITIVE_INT_REGEX.test(value)) {
        errors[key] = intl.formatMessage({ id: 'Please enter a positive whole number.' });
      } else if (Number(value) > ZIGI_MAX_RECOMMENDED_EXPIRY_MINUTES) {
        warnings[key] = intl.formatMessage({ id: 'ZIGI expiry above recommended warning' });
      }
    }
  });

  // The backend ignores the mask when it is sent back, so typing it counts as leaving the field empty.
  const typedSecret = (key: ZigiSecretKey): string => {
    const value = secrets[key].trim();
    return value === ZIGI_MASKED_VALUE ? '' : value;
  };
  const hasSecret = (key: ZigiSecretKey) => secretStatus[key] === 'configured' || typedSecret(key) !== '';
  const isComplete = ZIGI_REQUIRED_TO_ENABLE.every(key =>
    isSecretKey(key) ? hasSecret(key) : values[key].trim() !== '',
  );
  const switchDisabled = !enabled && !isComplete;

  const buildChangedSettings = (): ChangedSettings => {
    const changed: ChangedSettings = {};
    ZIGI_PLAIN_KEYS.forEach(key => {
      const value = normalizePlain(key, values[key]);
      if (value === initialValues[key]) return;
      changed[key] = key === 'expiryTimeMinutes' && value ? Number(value) : value;
    });
    ZIGI_SECRET_KEYS.forEach(key => {
      const value = typedSecret(key);
      if (value) changed[key] = value;
    });
    return changed;
  };

  const isDirty = enabled !== initialEnabled || Object.keys(buildChangedSettings()).length > 0;

  // The refetch triggered by the PUT must not overwrite what the admin is typing, so the
  // saved state is taken from the form itself instead of re-reading the response.
  const markSaved = () => {
    const saved = recordFrom(ZIGI_PLAIN_KEYS, key => normalizePlain(key, values[key]));
    setValues(saved);
    setInitialValues(saved);
    setInitialEnabled(enabled);
    setSecretStatus(prev => recordFrom(ZIGI_SECRET_KEYS, key => (typedSecret(key) ? 'configured' : prev[key])));
    setSecrets(emptySecrets());
  };

  const adminErrorMessage = (error: unknown): string => {
    const data = (error as { data?: Partial<ZigiAdminError> & { message?: unknown } })?.data;
    if (data?.code === 'ZIGI_NOT_CONFIGURED') return intl.formatMessage({ id: 'ZIGI not configured' });
    if (data?.code === ZIGI_WEBHOOK_KEY_MISSING_CODE) {
      return intl.formatMessage({ id: 'Save a Webhook Key before registering the webhook.' });
    }
    if (Number(data?.zigiCode) === ZIGI_WEBHOOK_DUPLICATE_CODE) {
      return intl.formatMessage({ id: 'ZIGI webhook already registered' });
    }
    const message = Array.isArray(data?.message) ? data?.message.join('. ') : data?.message;
    if (typeof message === 'string' && message) {
      return data?.zigiCode ? `${message} (${data.zigiCode})` : message;
    }
    return intl.formatMessage({ id: 'The ZIGI request could not be completed.' });
  };

  const requireSavedForm = (): boolean => {
    if (!isDirty) return true;
    toast.info({ title: intl.formatMessage({ id: 'Save your changes before using this action.' }) });
    return false;
  };

  const onCancel = () => {
    setEnabled(initialEnabled);
    setValues(initialValues);
    setSecrets(emptySecrets());
    setBusinesses(null);
  };

  const doSave = (changedSettings: ChangedSettings) =>
    withLoading(async () => {
      try {
        await updateIntegration({
          slug: ZIGI_SLUG,
          body: {
            ...(enabled !== initialEnabled ? { enabled } : {}),
            ...(Object.keys(changedSettings).length ? { settings: changedSettings } : {}),
          },
        }).unwrap();
        markSaved();
        setBusinesses(null);
        toast.success({ title: intl.formatMessage({ id: 'ZIGI configuration updated successfully!' }) });
      } catch (error) {
        toast.error({ title: adminErrorMessage(error) });
      }
    });

  const onSave = () => {
    if (Object.keys(errors).length) {
      toast.error({ title: intl.formatMessage({ id: 'Please fix the highlighted fields.' }) });
      return;
    }
    if (enabled && !isComplete) {
      toast.error({ title: intl.formatMessage({ id: 'ZIGI incomplete while enabled' }) });
      return;
    }
    const changedSettings = buildChangedSettings();
    if (!isDirty) {
      toast.info({ title: intl.formatMessage({ id: 'There are no changes to save.' }) });
      return;
    }
    const notes = [intl.formatMessage({ id: 'ZIGI save warning' })];
    if ('webhookKey' in changedSettings) notes.unshift(intl.formatMessage({ id: 'ZIGI webhook key save warning' }));
    if (warnings.expiryTimeMinutes) notes.unshift(warnings.expiryTimeMinutes);
    Modal.confirm({
      title: intl.formatMessage({ id: 'Save changes' }),
      content: notes.join(' '),
      okText: intl.formatMessage({ id: 'Save' }),
      cancelText: intl.formatMessage({ id: 'Cancel' }),
      className: 'integration_confirm_modal',
      onOk: () => doSave(changedSettings),
    });
  };

  const onListBusinesses = () => {
    if (!requireSavedForm()) return;
    withLoading(async () => {
      try {
        const res = await listBusinesses().unwrap();
        setBusinesses(res.results ?? []);
        if (!res.results?.length)
          toast.info({ title: intl.formatMessage({ id: 'No businesses found for these credentials.' }) });
      } catch (error) {
        toast.error({ title: adminErrorMessage(error) });
      }
    });
  };

  const openWebhookModal = () => {
    if (!requireSavedForm()) return;
    if (secretStatus.webhookKey !== 'configured') {
      toast.info({ title: intl.formatMessage({ id: 'Save a Webhook Key before registering the webhook.' }) });
      return;
    }
    setWebhookUrl(defaultWebhookUrl());
    setWebhookModalOpen(true);
  };

  const trimmedWebhookUrl = webhookUrl.trim();
  const webhookUrlError = (() => {
    if (!trimmedWebhookUrl) return undefined;
    if (!isHttpsUrl(trimmedWebhookUrl)) return intl.formatMessage({ id: 'The URL must start with https://' });
    if (trimmedWebhookUrl.length > ZIGI_WEBHOOK_URL_MAX_LENGTH) {
      return intl.formatMessage({ id: 'Maximum {max} characters.' }, { max: ZIGI_WEBHOOK_URL_MAX_LENGTH });
    }
    return undefined;
  })();

  const onRegisterWebhook = () =>
    withLoading(async () => {
      try {
        await registerWebhook({ url: trimmedWebhookUrl }).unwrap();
        setWebhookModalOpen(false);
        toast.success({ title: intl.formatMessage({ id: 'Webhook registered in ZIGI.' }) });
      } catch (error) {
        toast.error({ title: adminErrorMessage(error) });
      }
    });

  const label = (id: string, badge?: React.ReactNode) => (
    <span className="title" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
      <FormattedMessage id={id} />
      {badge}
    </span>
  );

  const plainField = (key: ZigiPlainKey, labelId: string, hintId: string, placeholder: string, maxLength?: number) => (
    <Form.Item
      label={label(labelId)}
      validateStatus={errors[key] ? 'error' : warnings[key] ? 'warning' : undefined}
      help={errors[key] ?? warnings[key]}
      extra={<FormattedMessage id={hintId} />}
      style={fieldStyle}
    >
      <Input
        id={`integration-zigi-${key}`}
        value={values[key]}
        placeholder={placeholder}
        maxLength={maxLength}
        autoComplete="off"
        spellCheck={false}
        onChange={e => setValues(prev => ({ ...prev, [key]: e.target.value }))}
      />
    </Form.Item>
  );

  const secretField = (key: ZigiSecretKey, labelId: string, hintId: string) => {
    const configured = secretStatus[key] === 'configured';
    return (
      <Form.Item
        label={label(
          labelId,
          <Tag color={configured ? 'success' : 'default'} style={{ margin: 0 }}>
            <FormattedMessage id={configured ? 'ZIGI secret configured' : 'ZIGI secret missing'} />
          </Tag>,
        )}
        extra={
          <>
            <FormattedMessage id={hintId} /> {configured && <FormattedMessage id="ZIGI leave blank to keep" />}
          </>
        }
        style={fieldStyle}
      >
        <Input.Password
          id={`integration-zigi-${key}`}
          value={secrets[key]}
          autoComplete="new-password"
          placeholder={configured ? ZIGI_MASKED_VALUE : intl.formatMessage({ id: 'Enter a new value' })}
          onChange={e => setSecrets(prev => ({ ...prev, [key]: e.target.value }))}
        />
      </Form.Item>
    );
  };

  if (loadError) {
    return (
      <div className="integrations_layout">
        <Card style={cardStyle}>
          <p className="title">
            <FormattedMessage id="Could not load the ZIGI configuration." />
          </p>
          <button id="integration-zigi-retry" type="button" className="btn_clear" onClick={load}>
            <FormattedMessage id="Retry" />
          </button>
        </Card>
      </div>
    );
  }

  const statusCardStyle = {
    ...cardStyle,
    borderColor: enabled ? '#b7eb8f' : '#ffccc7',
    background: enabled ? '#f6ffed' : '#fff2f0',
  };

  return (
    <div className="integrations_layout">
      <Loading visible={isLoading} />

      <div className="integrations_status_card">
        <Card style={statusCardStyle}>
          <div className="status_row">
            <div className="status_info">
              <p className="title" style={{ marginBottom: 4 }}>
                <FormattedMessage id="Enable ZIGI payments" />
              </p>
              <div className="status_badge">
                {enabled ? (
                  <CheckCircleOutline style={{ fontSize: 15, color: '#52c41a' }} />
                ) : (
                  <RadioButtonUnchecked style={{ fontSize: 15, color: '#ff4d4f' }} />
                )}
                <Tag color={enabled ? 'success' : 'error'} style={{ margin: 0 }}>
                  <FormattedMessage id={enabled ? 'Active' : 'Inactive'} />
                </Tag>
              </div>
              <p className="description" style={{ marginBottom: 0 }}>
                <FormattedMessage id="ZIGI disabled hint" />
              </p>
              {switchDisabled && (
                <p className="description" style={{ marginBottom: 0, color: '#faad14' }}>
                  <FormattedMessage id="Complete API URL, API Key and Business Code to enable the integration." />
                </p>
              )}
            </div>
            <Switch id="integration-zigi-enabled" checked={enabled} disabled={switchDisabled} onChange={setEnabled} />
          </div>
        </Card>
      </div>

      <div className="integrations_grid">
        <div className="integrations_left_col">
          <Card title={<FormattedMessage id="ZIGI credentials" />} size="small" style={cardStyle}>
            <Form layout="vertical" colon={false}>
              {plainField('apiUrl', 'API URL', 'ZIGI API URL hint', 'https://api.eaglehubsqa.com')}
              {secretField('apiKey', 'API Key', 'ZIGI API Key hint')}
              {plainField(
                'businessCode',
                'Business Code',
                'ZIGI Business Code hint',
                '',
                ZIGI_BUSINESS_CODE_MAX_LENGTH,
              )}
              <button
                id="integration-zigi-list-businesses"
                type="button"
                className="btn_clear"
                style={{ marginTop: 0 }}
                onClick={onListBusinesses}
              >
                <FormattedMessage id="List businesses" />
              </button>
              {businesses && businesses.length > 0 && (
                <Form.Item label={label('Select a business to fill the code')} style={{ ...fieldStyle, marginTop: 14 }}>
                  <Select
                    id="integration-zigi-business-select"
                    style={{ width: '100%' }}
                    placeholder={intl.formatMessage({ id: 'Select a business to fill the code' })}
                    value={
                      businesses.some(b => b.businessCode === values.businessCode) ? values.businessCode : undefined
                    }
                    onChange={(code: string) => setValues(prev => ({ ...prev, businessCode: code }))}
                    getPopupContainer={trigger => trigger.parentNode}
                  >
                    {businesses.map(b => (
                      <Option key={b.businessId} value={b.businessCode}>
                        {b.businessName} ({b.businessCode})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Form>
          </Card>
        </div>

        <div className="integrations_right_col">
          <Card title={<FormattedMessage id="Webhook" />} size="small" style={cardStyle}>
            <Alert
              type="info"
              showIcon
              style={fieldStyle}
              message={intl.formatMessage({ id: 'ZIGI webhook match notice' })}
            />
            <Form layout="vertical" colon={false}>
              {secretField('webhookKey', 'Webhook Key', 'ZIGI Webhook Key hint')}
            </Form>
            <button
              id="integration-zigi-register-webhook"
              type="button"
              className="btn_clear"
              style={{ marginTop: 0 }}
              onClick={openWebhookModal}
            >
              <FormattedMessage id="Register webhook in ZIGI" />
            </button>
          </Card>

          <Card title={<FormattedMessage id="Options" />} size="small" style={cardStyle}>
            <Form layout="vertical" colon={false}>
              {plainField('currency', 'Currency', 'ZIGI currency hint', 'GTQ', ZIGI_CURRENCY_LENGTH)}
              {plainField('expiryTimeMinutes', 'Expiry time (minutes)', 'ZIGI expiry hint', '10')}
              {plainField('redirectUrl', 'Redirect URL', 'ZIGI redirect URL hint', ZIGI_APP_RETURN_URL)}
            </Form>
          </Card>
        </div>
      </div>

      <div className="integrations_actions">
        <Button id="integration-zigi-save" onClick={onSave}>
          <FormattedMessage id="Save" />
          <ArrowForward />
        </Button>
        <p id="integration-zigi-cancel" className="integrations_cancel_btn" onClick={onCancel}>
          <FormattedMessage id="Cancel" />
        </p>
      </div>

      <Modal
        open={webhookModalOpen}
        title={intl.formatMessage({ id: 'Register webhook in ZIGI' })}
        okText={intl.formatMessage({ id: 'Register' })}
        cancelText={intl.formatMessage({ id: 'Cancel' })}
        okButtonProps={{
          id: 'integration-zigi-register-webhook-confirm',
          disabled: !trimmedWebhookUrl || !!webhookUrlError || isLoading,
        }}
        className="integration_confirm_modal"
        onOk={onRegisterWebhook}
        onCancel={() => setWebhookModalOpen(false)}
        destroyOnClose
      >
        <p className="description">
          <FormattedMessage id="ZIGI register webhook description" />
        </p>
        <Form layout="vertical" colon={false}>
          <Form.Item
            label={label('Webhook URL')}
            validateStatus={webhookUrlError ? 'error' : undefined}
            help={webhookUrlError}
            style={{ marginBottom: 0 }}
          >
            <Input
              id="integration-zigi-webhook-url"
              value={webhookUrl}
              maxLength={ZIGI_WEBHOOK_URL_MAX_LENGTH}
              autoComplete="off"
              spellCheck={false}
              onChange={e => setWebhookUrl(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ZigiConfig;
